import { Box, TextField, Typography, Alert, Skeleton } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTemplateStore } from '../store';
import { CreateAlertConfigType } from '../schema/AlertBuilderSchemaTypes';
import { DEFAULT_ALERT_TEMPLATE } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useClientDataOnTemplate';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import colors from 'src/utils/colors';
import { useAIGenerateTemplate } from '../hooks/useAlertsApi';
import { getAppName } from 'src/utils/app';

const getBackgroundColors = (isValidJson: boolean) => {
  if (isValidJson) {
    return '#2d3748';
  }
  return '#4a1e1e';
};

const AlertCustomTemplateTab = () => {
  const { alertTemplate, setAlertTemplate } = useTemplateStore();
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isValidJson, setIsValidJson] = useState(true);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [userInput, setUserInput] = useState('');

  // AI Generate Template hook
  const { aiGenerateAsync, isPending: isAIGenerating, error: aiError } = useAIGenerateTemplate();

  // Handle AI generation
  const handleGenerateAIAlert = async () => {
    if (!userInput.trim()) return;

    try {
      const result = await aiGenerateAsync({
        user_input: userInput,
        product: getAppName()?.toLowerCase() || 'atlas'
      });

      if (result.data?.success && result.data?.data) {
        // Wrap the API response in queryJSON structure since API returns query config directly
        const alertConfigWithQueryJSON = {
          queryJSON: result.data.data
        };

        // Update the template store with generated data
        setAlertTemplate(alertConfigWithQueryJSON);
        // Update the JSON input display
        setJsonInput(JSON.stringify(alertConfigWithQueryJSON, null, 2));
        setHasPendingChanges(false);
      }
    } catch (error) {
      console.error('Failed to generate AI alert:', error);
    }
  };

  // Handle reset to default template
  const handleResetToDefault = () => {
    setAlertTemplate(DEFAULT_ALERT_TEMPLATE as Partial<CreateAlertConfigType>);
    setJsonInput(JSON.stringify(DEFAULT_ALERT_TEMPLATE, null, 2));
    setHasPendingChanges(false);
  };

  // Initialize with default JSON structure
  useEffect(() => {
    const initialJson = Object.keys(alertTemplate).length > 0 ? alertTemplate : DEFAULT_ALERT_TEMPLATE;

    setJsonInput(JSON.stringify(initialJson, null, 2));

    // If store is empty, save the default template to store immediately
    if (Object.keys(alertTemplate).length === 0) {
      setAlertTemplate(DEFAULT_ALERT_TEMPLATE as Partial<CreateAlertConfigType>);
    }
  }, [alertTemplate, setAlertTemplate]);

  // Debounced sync to store - only sync after 2 seconds of no changes
  useEffect(() => {
    if (hasPendingChanges) {
      const timeoutId = setTimeout(() => {
        if (isValidJson && jsonInput.trim()) {
          try {
            const parsedJson = JSON.parse(jsonInput);
            setAlertTemplate(parsedJson as Partial<CreateAlertConfigType>);
            setHasPendingChanges(false); // Mark changes as saved
          } catch (error) {
            // This shouldn't happen since we check validity before setting isValidJson
          }
        }
      }, 1000); // 1 second delay

      // Cleanup timeout on new changes or component unmount
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [jsonInput, isValidJson, setAlertTemplate, hasPendingChanges]);

  const handleJsonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setJsonInput(value);
    setHasPendingChanges(true); // Mark that we have unsaved changes

    // Validate JSON
    try {
      if (value.trim()) {
        JSON.parse(value);
        setJsonError(null);
        setIsValidJson(true);
      } else {
        setJsonError(null);
        setIsValidJson(true);
      }
    } catch (error) {
      setJsonError(`Invalid JSON: ${error instanceof Error ? error.message : 'Syntax error'}`);
      setIsValidJson(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();

      const target = event.target as HTMLTextAreaElement;
      const start = target.selectionStart;
      const end = target.selectionEnd;

      if (event.shiftKey) {
        // Shift+Tab: Remove indentation (up to 2 spaces)
        const beforeCursor = jsonInput.substring(0, start);
        const afterCursor = jsonInput.substring(end);

        // Check if there are spaces before cursor to remove
        if (beforeCursor.endsWith('  ')) {
          // Remove 2 spaces
          const newValue = beforeCursor.slice(0, -2) + afterCursor;
          setJsonInput(newValue);
          setHasPendingChanges(true); // Mark that we have unsaved changes
          setTimeout(() => {
            target.selectionStart = target.selectionEnd = start - 2;
          }, 0);
        } else if (beforeCursor.endsWith(' ')) {
          // Remove 1 space
          const newValue = beforeCursor.slice(0, -1) + afterCursor;
          setJsonInput(newValue);
          setHasPendingChanges(true); // Mark that we have unsaved changes
          setTimeout(() => {
            target.selectionStart = target.selectionEnd = start - 1;
          }, 0);
        }
      } else {
        // Tab: Add indentation (2 spaces)
        const newValue = jsonInput.substring(0, start) + '  ' + jsonInput.substring(end);
        setJsonInput(newValue);
        setHasPendingChanges(true); // Mark that we have unsaved changes

        // Move cursor to after the inserted spaces
        setTimeout(() => {
          target.selectionStart = target.selectionEnd = start + 2;
        }, 0);
      }
    }
  };

  const formatJson = () => {
    try {
      if (jsonInput.trim()) {
        const parsed = JSON.parse(jsonInput);
        const formatted = JSON.stringify(parsed, null, 2);
        setJsonInput(formatted);
      }
    } catch (error) {
      // Error is already handled in handleJsonChange
    }
  };

  const handleBeautify = () => {
    if (isValidJson && jsonInput.trim()) {
      formatJson();
      setHasPendingChanges(true); // Mark as changed to trigger save
    }
  };

  return (
    <Box sx={{ padding: 0 }}>
      {/* AI Alert Generation Section */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Generate Alert from Description
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
          Describe your alert in natural language and let AI generate the configuration for you.
        </Typography>

        <Box
          sx={{
            border: `1px solid ${colors.borderGrey}`,
            borderRadius: '8px',
            backgroundColor: 'white',
            marginBottom: 2
          }}
        >
          <TextField
            multiline
            fullWidth
            rows={3}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder="e.g., New product in the Cell Phones category has sales greater than $10,000 for the first time"
            variant="outlined"
            sx={{
              '& .MuiInputBase-root': {
                border: 'none',
                borderRadius: '8px',
                minHeight: '90px',
                alignItems: 'flex-start',
                padding: '16px',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              },
              '& .MuiInputBase-input': {
                padding: 0,
                fontSize: '14px',
                lineHeight: '20px'
              }
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SlButton
            variant="contained"
            onClick={handleGenerateAIAlert}
            disabled={!userInput.trim() || isAIGenerating}
            sx={{ minWidth: '160px' }}
          >
            {isAIGenerating ? 'Generating...' : 'Generate AI Alert'}
          </SlButton>

          <SlButton
            variant="normal"
            onClick={handleResetToDefault}
            disabled={isAIGenerating}
            sx={{ minWidth: '120px' }}
          >
            Reset to Default
          </SlButton>

          {isAIGenerating && (
            <Typography variant="body2" color="error" sx={{ fontSize: '12px', fontStyle: 'italic' }}>
              Generating alert could take about 30 seconds...
            </Typography>
          )}

          {aiError && !isAIGenerating && (
            <Typography variant="body2" color="error">
              Failed to generate alert: {aiError.toString()}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="h6" sx={{ marginBottom: 2 }}>
        Custom Template
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
        Enter your custom alert template configuration in JSON format. Changes will be automatically saved.
        {jsonInput.split('\n').length > 190 && (
          <Typography component="span" color="warning.main" sx={{ display: 'block', marginTop: 1, fontWeight: 500 }}>
            ⚠️ Approaching maximum height ({jsonInput.split('\n').length}/200 lines)
          </Typography>
        )}
      </Typography>

      <Typography
        variant="caption"
        color={hasPendingChanges ? 'warning.main' : 'success.main'}
        sx={{
          marginTop: 1,
          display: 'block',
          fontWeight: 500
        }}
      >
        {hasPendingChanges ? '⏳ Changes will be saved in 2 seconds...' : '✅ Changes are saved'}
      </Typography>

      {jsonError && (
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {jsonError}
        </Alert>
      )}

      {isAIGenerating ? (
        <Box sx={{ padding: 2, border: `1px solid ${colors.borderGrey}`, borderRadius: '8px' }}>
          <Skeleton variant="text" width="100%" height={20} />
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="90%" height={20} />
          <Skeleton variant="text" width="70%" height={20} />
          <Skeleton variant="text" width="85%" height={20} />
          <Skeleton variant="text" width="95%" height={20} />
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="75%" height={20} />
          <Skeleton variant="text" width="88%" height={20} />
          <Skeleton variant="text" width="65%" height={20} />
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {/* Beautify Button - Absolute positioned */}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              pointerEvents: 'auto'
            }}
          >
            <SlButton
              variant="tertiary"
              onClick={handleBeautify}
              disabled={!isValidJson || !jsonInput.trim()}
              sx={{ fontSize: '12px', padding: '4px 8px', color: 'white', fontColor: colors.darkBlue }}
            >
              Beautify JSON
            </SlButton>
          </Box>

          <TextField
            multiline
            fullWidth
            minRows={3}
            maxRows={40}
            value={jsonInput}
            onChange={handleJsonChange}
            onKeyDown={handleKeyDown}
            onBlur={formatJson}
            error={!isValidJson}
            variant="outlined"
            placeholder="Enter JSON configuration..."
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '14px',
                lineHeight: 1.5,
                color: colors.white,
                backgroundColor: getBackgroundColors(isValidJson),
                padding: 0,
                margin: 0
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: getBackgroundColors(isValidJson),
                borderColor: isValidJson ? colors.darkGrey : colors.redBright,
                padding: 0,
                '&:hover': {
                  borderColor: isValidJson ? colors.labelGrey : colors.redBright,
                  backgroundColor: getBackgroundColors(isValidJson)
                },
                '&.Mui-focused': {
                  borderColor: isValidJson ? colors.brightBlue : colors.redBright,
                  backgroundColor: getBackgroundColors(isValidJson)
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isValidJson ? colors.grey : colors.redBright
              },
              '& .MuiInputBase-input::placeholder': {
                color: colors.atlasDarkGrey,
                opacity: 1
              },
              '& .MuiInputBase-inputMultiline': {
                padding: '12px !important',
                overflow: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#4a5568 #2d3748',
                '&::-webkit-scrollbar': {
                  width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#2d3748'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#4a5568',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#718096'
                  }
                }
              }
            }}
            helperText={
              !isValidJson
                ? 'Please fix the JSON syntax errors'
                : hasPendingChanges
                ? `Saving in 2 seconds... Lines: ${jsonInput.split('\n').length}${
                    jsonInput.split('\n').length > 40 ? ' (scroll to see more)' : ''
                  }`
                : `Saved ✓ Lines: ${jsonInput.split('\n').length}${
                    jsonInput.split('\n').length > 40 ? ' (scroll to see more)' : ''
                  } • Use Tab/Shift+Tab for indentation`
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default AlertCustomTemplateTab;
